import { useEffect, useState } from 'react';
import type { CallState } from '../services/WebRTCCallService';
import { webRTCCallService } from '../services/WebRTCCallService';
import {
  cacheCallStateForCrm,
  getCachedCallStateFromStorage,
  isAgentPhoneWindow,
  requestCallStateSync,
  subscribeAgentPhoneMessages,
} from '../services/agentPhone/agentPhoneBridge';

const idleState: CallState = {
  status: 'idle',
  isConnected: false,
  isMuted: false,
  isSpeakerOn: true,
  isOnHold: false,
  callDuration: 0,
  direction: 'none',
  callerInfo: null,
  clientId: null,
  clientName: null,
  clientPhone: null,
  callSid: null,
  queueTime: 0,
  channel: 'direct',
};

/**
 * Call state for CRM tabs: synced from the agent phone window via BroadcastChannel.
 * In the phone window, reads directly from WebRTCCallService.
 */
export function useAgentPhoneCallState(): CallState {
  const [state, setState] = useState<CallState>(() => {
    if (isAgentPhoneWindow()) {
      return webRTCCallService.getCallState();
    }
    return getCachedCallStateFromStorage() ?? idleState;
  });

  useEffect(() => {
    if (isAgentPhoneWindow()) {
      const listener = (next: CallState) => setState(next);
      setState(webRTCCallService.getCallState());
      webRTCCallService.addCallStateListener(listener);
      return () => webRTCCallService.removeCallStateListener(listener);
    }

    // Ask the phone pop-out for its current state immediately on mount so
    // the CRM's AgentPhoneDock shows the active call snap-fast rather than
    // waiting for the next organic CALL_STATE event.
    requestCallStateSync();

    return subscribeAgentPhoneMessages((message) => {
      if (message.type === 'CALL_STATE' || message.type === 'STATE_SYNC') {
        cacheCallStateForCrm(message.state);
        setState(message.state);
      }
    });
  }, []);

  return state;
}
